/**
 * How a value becomes a cell.
 *
 * Pure formatting, deliberately kept out of ChatRichBlocks: that module pulls
 * in react-markdown (ESM-only), so anything living there cannot be unit-tested
 * — which is how a currency table shipped bare numbers with nobody noticing.
 * These four functions are where units are honoured or lost, so they get to be
 * testable on their own.
 */

/** A number Postgres sent as a string (NUMERIC, every AVG). */
export const NUMERIC_STRING_RE = /^-?\d+(\.\d+)?(e[-+]?\d+)?$/i;

/**
 * A percent on the 0–100 scale. A column NAMED a percent (`change_pct`) already
 * is one, however small: a −0.83 change read "−83.2%" when it was guessed to
 * be a fraction. Only a rate/share, or a column whose name says nothing, is
 * read as a fraction when it is within ±1. The server's asPercent, mirrored.
 */
const PCT_NAMED_RE = /(^|_)(pct|percent|percentage)(_|$)/i;
export function percentOf(v, key) {
  if (PCT_NAMED_RE.test(String(key || ""))) return v;
  return Math.abs(v) <= 1 ? v * 100 : v;
}

/**
 * Anything that is not a number or a date, as text a person reads. An object
 * cell printed its JSON — `{"model":"gpt-5","calls":3}` — and an array of
 * names printed with bare commas. Short objects read `model: gpt-5 · calls: 3`.
 */
export function valueText(v) {
  if (v == null) return "";
  let x = v;
  if (typeof x === "string") {
    const t = x.trim();
    if (!/^[[{]/.test(t)) return x;
    try { x = JSON.parse(t); } catch { return x; }
  }
  if (Array.isArray(x)) return x.map((e) => (e != null && typeof e === "object" ? valueText(e) : String(e ?? ""))).filter(Boolean).join(", ");
  if (typeof x === "object") {
    const parts = Object.entries(x)
      .filter(([, val]) => val != null && val !== "")
      .map(([k, val]) => `${k.replace(/_/g, " ")}: ${typeof val === "object" ? valueText(val) : String(val)}`);
    const text = parts.join(" · ");
    return text.length > 120 ? `${text.slice(0, 119)}…` : text;
  }
  return String(x);
}

/** Column keys that are milliseconds by convention, when nothing says so. */
export const MS_KEY_RE = /(_ms$|latency|duration)/i;

export function formatValue(v) {
  if (typeof v === "number" && Number.isFinite(v)) {
    const a = Math.abs(v);
    if (a >= 1_000_000) return (v / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
    if (a >= 10_000) return (v / 1_000).toFixed(1).replace(/\.0$/, "") + "k";
    if (!Number.isInteger(v)) return v.toFixed(2).replace(/\.?0+$/, "");
    return v.toLocaleString();
  }
  return String(v ?? "");
}

export function formatDurationMs(ms) {
  if (!Number.isFinite(ms)) return String(ms);
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1).replace(/\.0$/, "")}s`;
  const m = Math.floor(ms / 60_000);
  const sec = Math.round((ms % 60_000) / 1000);
  return sec ? `${m}m ${sec}s` : `${m}m`;
}

/**
 * A column type qualifies a value; a spec-level unit qualifies the table.
 *
 * "number" and "string" are NOT units — they say "this is numeric/textual",
 * which is exactly what the spec's unit is there to qualify. Treating any
 * column type as final is how "What did each model cost us?" rendered
 * `gpt-5.4-mini  6.67` on a spec that declared currency. Only a real unit
 * type (currency / percent / duration_ms) outranks the spec; the _ms/latency
 * key convention is the safety net for rows that arrived without either.
 */
// An ISO timestamp, or a Date: a time cell reads in the viewer's own clock,
// short, on one line — "Sep 3, 14:05" — never "2026-09-03T14:05:12.000Z" and
// never "at 03:32 UTC" (Akshit, 3 Sep: convert to local, drop the UTC).
const ISO_RE = /^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;
export function formatTimeLocal(v) {
  const d = v instanceof Date ? v : new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  const now = new Date();
  const sameYear = d.getFullYear() === now.getFullYear();
  // Midnight in the viewer's clock is a day. A day bucket built in their
  // timezone arrives as "2026-10-14T13:00:00Z" for Melbourne, and read
  // "15 Oct, 00:00" in every row.
  const hasTime = !(d.getHours() === 0 && d.getMinutes() === 0);
  return d.toLocaleString(undefined, {
    month: "short", day: "numeric", ...(sameYear ? {} : { year: "numeric" }),
    ...(hasTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  });
}

export function cellDisplay(v, type, colKey, specUnit) {
  if (v == null) return "";
  if (v instanceof Date || (type === "date" || type === "time" || /(_at|_time|^time$|^date$|^day$|^bucket$)/i.test(String(colKey || ""))) && typeof v === "string" && ISO_RE.test(v.trim())) {
    return formatTimeLocal(v);
  }
  const columnUnit = type && type !== "number" && type !== "string" ? type : null;
  const effective = columnUnit
    || (typeof v === "number" && specUnit)
    || (typeof v === "number" && MS_KEY_RE.test(String(colKey || "")) ? "duration_ms" : null);
  if (effective === "percent" && typeof v === "number") return percentOf(v, colKey).toFixed(1) + "%";
  if (effective === "currency" && typeof v === "number") {
    const a = Math.abs(v);
    // From a dollar up, cents and all ("$4.70"), as the answer prints it.
    if (a >= 1) return "$" + v.toFixed(2);
    const dp = a >= 0.01 || a === 0 ? 4 : Math.min(10, 2 - Math.floor(Math.log10(a)));
    return "$" + (a === 0 ? "0" : v.toFixed(dp).replace(/(\.\d{2}\d*?)0+$/, "$1"));
  }
  if (effective === "duration_ms" && typeof v === "number") return formatDurationMs(v);
  if (typeof v === "number") return formatValue(v);
  return valueText(v);
}
